import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, LayoutGrid, MessageSquareQuote, Settings as SettingsIcon, Inbox, Loader2, ExternalLink, KeyRound, Users, FileText, Magnet, CalendarCheck, Newspaper, Megaphone, Languages, ShoppingBag, Type, BookUser, Palette, Share2, MailCheck, Clapperboard, UserPlus, Instagram, Activity } from "lucide-react";
import { useAdminT, setAdminLang } from "./adminI18n";
import { CoursesTab } from "./CoursesTab";
import { ConsultationsTab } from "./ConsultationsTab";
import { PostsTab } from "./PostsTab";
import { BannersTab } from "./BannersTab";
import { TestimonialsTab } from "./TestimonialsTab";
import { SettingsTab } from "./SettingsTab";
import { MessagesTab } from "./MessagesTab";
import { PasswordTab } from "./PasswordTab";
import { AccessTab } from "./AccessTab";
import { ActivityTab } from "./ActivityTab";
import { RequestsTab } from "./RequestsTab";
import { TextsTab } from "./TextsTab";
import { PagesTab } from "./PagesTab";
import { LeadsTab } from "./LeadsTab";
import { ContactsTab } from "./ContactsTab";
import { AppearanceTab } from "./AppearanceTab";
import { MarketingTab } from "./MarketingTab";
import { AutomationTab } from "./AutomationTab";
import { LOGO_URL } from "../../config";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("themaya_token")}`,
});

export const formatApiError = (detail) => {
  if (detail == null) return "حدث خطأ، حاولي مجدداً";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export const AdminLangToggle = () => {
  const { lang } = useAdminT();
  return (
    <button
      type="button"
      data-testid="admin-lang-toggle"
      onClick={() => setAdminLang(lang === "ar" ? "en" : "ar")}
      className="flex items-center gap-2 border border-luxury-border text-luxury-gray hover:text-luxury-gold hover:border-luxury-gold px-4 py-2 rounded-full text-sm transition-colors"
    >
      <Languages className="w-4 h-4" strokeWidth={1.5} />
      {lang === "ar" ? "EN" : "عربي"}
    </button>
  );
};

const StatsBar = () => {
  const { t } = useAdminT();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () =>
      axios.get(`${API}/admin/stats`, { headers: authHeaders() }).then((res) => alive && setStats(res.data)).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  if (!stats) return null;
  const cards = [
    { id: "emails-today", icon: MailCheck, value: stats.emails_today, label: t("رسائل اليوم"), sub: stats.emails_failed_today > 0 ? `${stats.emails_failed_today} ${t("فشل")}` : "" },
    { id: "videos-sd", icon: Clapperboard, value: `${stats.videos_sd_ready}/${stats.videos_total}`, label: t("نسخ موفّرة جاهزة"), sub: stats.videos_sd_working > 0 ? `${stats.videos_sd_working} ${t("قيد التحويل")}` : "" },
    { id: "contacts-week", icon: UserPlus, value: stats.contacts_week, label: t("جهات جديدة هذا الأسبوع"), sub: "" },
    { id: "contacts-total", icon: Users, value: stats.contacts_total, label: t("إجمالي جهات الاتصال"), sub: "" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="stats-bar">
      {cards.map((c) => (
        <div key={c.id} data-testid={`stat-${c.id}`} className="border border-luxury-border bg-luxury-surface p-4 md:p-5">
          <c.icon className="w-5 h-5 text-luxury-gold mb-3" strokeWidth={1.5} />
          <p className="font-serif text-2xl md:text-3xl text-luxury-bone">{c.value}</p>
          <p className="text-luxury-gray text-xs mt-1">{c.label}</p>
          {c.sub && <p className="text-luxury-gold/80 text-[11px] mt-1">{c.sub}</p>}
        </div>
      ))}
    </div>
  );
};

const TABS = [
  { id: "courses", label: "الدورات", icon: LayoutGrid },
  { id: "consultations", label: "الاستشارات الخاصة", icon: CalendarCheck },
  { id: "testimonials", label: "آراء العملاء", icon: MessageSquareQuote },
  { id: "settings", label: "التصميم والمحتوى", icon: SettingsIcon },
  { id: "messages", label: "الرسائل الواردة", icon: Inbox },
  { id: "access", label: "الصلاحيات", icon: Users },
  { id: "activity", label: "نشاط العميلات", icon: Activity },
  { id: "requests", label: "طلبات الشراء", icon: ShoppingBag },
  { id: "texts", label: "نصوص الموقع", icon: Type },
  { id: "pages", label: "صفحات الهبوط", icon: FileText },
  { id: "banners", label: "لافتات الفعاليات", icon: Megaphone },
  { id: "blog", label: "المدونة", icon: Newspaper },
  { id: "leads", label: "العملاء المحتملون", icon: Magnet },
  { id: "contacts", label: "جهات الاتصال", icon: BookUser },
  { id: "marketing", label: "التسويق", icon: Share2 },
  { id: "automation", label: "أتمتة إنستغرام", icon: Instagram },
  { id: "appearance", label: "المظهر", icon: Palette },
  { id: "password", label: "كلمة المرور", icon: KeyRound },
];

export const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem("themaya_token") || "");
  const [checking, setChecking] = useState(!!localStorage.getItem("themaya_token"));
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("courses");
  const [authRetry, setAuthRetry] = useState(0);
  const { t, lang } = useAdminT();
  const dir = lang === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    if (!token) return;
    setChecking(true);
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then(() => {
        setAuthed(true);
        setChecking(false);
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("themaya_token");
          setToken("");
          setChecking(false);
        } else {
          setTimeout(() => setAuthRetry((r) => r + 1), 6000);
        }
      });
  }, [token, authRetry]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { timeout: 20000 });
      localStorage.setItem("themaya_token", res.data.access_token);
      setToken(res.data.access_token);
      setAuthed(true);
      toast.success("أهلاً بك مايا");
    } catch (err) {
      const status = err.response?.status;
      if (!status || status >= 500) {
        setError("الخادم يستعد حالياً — انتظري دقيقة ثم أعيدي المحاولة");
      } else {
        setError(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("themaya_token");
    setToken("");
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-luxury-black" dir={dir} data-testid="admin-connecting">
        <Loader2 className="w-8 h-8 text-luxury-gold animate-spin" />
        <p className="text-luxury-gray text-sm">{t("جارٍ الاتصال بالخادم...")}</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-luxury-black px-4 relative" dir={dir} data-testid="admin-login-page">
        <div className="absolute top-6 end-6"><AdminLangToggle /></div>
        <form onSubmit={login} className="w-full max-w-md bg-luxury-surface border border-luxury-border p-10 space-y-6" data-testid="admin-login-form">
          <div className="text-center">
            <img src={LOGO_URL} alt="TheMaya" className="h-10 mx-auto mb-4" />
            <h1 className="font-serif text-3xl text-luxury-bone">{t("لوحة التحكم")}</h1>
            <p className="text-luxury-gray text-sm mt-2">{t("تسجيل دخول المشرفة")}</p>
          </div>
          <div>
            <label className="block text-sm text-luxury-gray mb-2" htmlFor="admin-email">{t("البريد الإلكتروني")}</label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-luxury-gray mb-2" htmlFor="admin-password">{t("كلمة المرور")}</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              dir="ltr"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold"
              required
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-red-400 text-sm">{error}</p>}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full bg-luxury-gold text-luxury-black font-semibold py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("دخول")}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-luxury-black" dir={dir} data-testid="admin-dashboard">
      <header className="border-b border-luxury-border bg-luxury-dark">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="TheMaya" className="h-8" />
            <h1 className="font-serif text-2xl text-luxury-bone hidden sm:block">{t("لوحة تحكم TheMaya")}</h1>
          </div>
          <div className="flex items-center gap-3">
            <AdminLangToggle />
            <a data-testid="admin-view-site" href="/" className="text-luxury-gray hover:text-luxury-gold text-sm flex items-center gap-2 transition-colors">
              <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
              {t("عرض الموقع")}
            </a>
            <button data-testid="admin-logout-button" onClick={logout} className="text-luxury-gray hover:text-red-400 text-sm flex items-center gap-2 transition-colors">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              {t("خروج")}
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex flex-wrap gap-2 pb-3">
          {TABS.map((tabDef) => (
            <button
              key={tabDef.id}
              data-testid={`admin-tab-${tabDef.id}`}
              onClick={() => setTab(tabDef.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm whitespace-nowrap border transition-colors ${
                tab === tabDef.id
                  ? "bg-luxury-gold text-luxury-black border-luxury-gold font-semibold"
                  : "border-luxury-border text-luxury-gray hover:text-luxury-gold hover:border-luxury-gold"
              }`}
            >
              <tabDef.icon className="w-4 h-4" strokeWidth={1.5} />
              {t(tabDef.label)}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <StatsBar />
        {tab === "courses" && <CoursesTab />}
        {tab === "consultations" && <ConsultationsTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "messages" && <MessagesTab />}
        {tab === "access" && <AccessTab />}
        {tab === "activity" && <ActivityTab />}
        {tab === "requests" && <RequestsTab />}
        {tab === "texts" && <TextsTab />}
        {tab === "pages" && <PagesTab />}
        {tab === "banners" && <BannersTab />}
        {tab === "blog" && <PostsTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "contacts" && <ContactsTab />}
        {tab === "marketing" && <MarketingTab />}
        {tab === "automation" && <AutomationTab />}
        {tab === "appearance" && <AppearanceTab />}
        {tab === "password" && <PasswordTab />}
      </main>
    </div>
  );
};
